import React from 'react'
import {
	EuiDescriptionList,
	EuiDescriptionListDescription,
	EuiDescriptionListTitle,
	EuiFlexGroup,
	EuiFlexItem,
	EuiTitle,
} from '@equipmentshare/ds2'
import { FullHeightPanel } from '../RequestDetails.styles'
import { AUTOMATIC_FULFILLING_INVENTORY_LABEL, TITLE_MARGIN } from '../RequestDetails.constants'
import { formatDateTime } from '../../../utils/formatters/date'
import { DemandRequestAssignmentStatus, DemandRequestStatusBadge } from '../utils/demandRequestStatus'
import RequestActionsMenu from './RequestActionsMenu'

type User = {
	id: string,
	firstName?: string | null,
	lastName?: string | null,
}

type DemandRequest = {
	id: string,
	// Null on a stock request that inventory-api routes automatically
	fulfillingInventory?: { id: string, name: string } | null,
	owningInventory?: { id: string } | null,
	assignedToUser?: User | null,
	assignedToUserId?: string | null,
	dateCompleted?: string | null,
	completedByUser?: User | null,
	dateCancelled?: string | null,
	cancelledByUser?: User | null,
	requestedByUser?: { id: string } | null,
	requestingUserId?: string | null,
	lineItems?: { neededBy?: string | null }[],
}

type FulfillmentPanelProps = {
	request: DemandRequest,
	canUserEditRequests: boolean,
	onMarkAsCompleted?: () => Promise<void>,
}

/**
 * Component for displaying fulfillment information panel with status and actions
 */
export default function FulfillmentPanel( { request, canUserEditRequests, onMarkAsCompleted }: FulfillmentPanelProps ) {
	// Check if the fulfiller cancelled the request (not the requester)
	const fulfillerCancelled = request.dateCancelled &&
		request.cancelledByUser &&
		( !request.requestedByUser || request.cancelledByUser.id !== request.requestedByUser.id )

	return (
		<FullHeightPanel hasBorder>
			<EuiFlexGroup justifyContent="spaceBetween" alignItems="center" gutterSize="s" responsive={false}>
				<EuiFlexItem grow={false}>
					<EuiTitle size="s">
						<h4 style={TITLE_MARGIN}>FULFILLMENT</h4>
					</EuiTitle>
				</EuiFlexItem>
				<EuiFlexItem grow={false}>
					<EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
						<EuiFlexItem grow={false}>
							<DemandRequestStatusBadge request={request} />
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<RequestActionsMenu request={request} canUserEditRequests={canUserEditRequests} onMarkAsCompleted={onMarkAsCompleted} />
						</EuiFlexItem>
					</EuiFlexGroup>
				</EuiFlexItem>
			</EuiFlexGroup>
			<EuiDescriptionList compressed>
				<EuiDescriptionListTitle>Fulfilled By</EuiDescriptionListTitle>
				<EuiDescriptionListDescription>
					{request.fulfillingInventory?.name ?? AUTOMATIC_FULFILLING_INVENTORY_LABEL}
				</EuiDescriptionListDescription>
				<EuiDescriptionListTitle>Assigned to</EuiDescriptionListTitle>
				<EuiDescriptionListDescription>
					{request.assignedToUser ? (
						<DemandRequestAssignmentStatus request={request} />
					) : (
						'—'
					)}
				</EuiDescriptionListDescription>
				{request.dateCompleted && (
					<>
						<EuiDescriptionListTitle>Completed</EuiDescriptionListTitle>
						<EuiDescriptionListDescription>
							{formatDateTime( request.dateCompleted )}
							{request.completedByUser && (
								<> by {request.completedByUser.firstName} {request.completedByUser.lastName}</>
							)}
						</EuiDescriptionListDescription>
					</>
				)}
				{fulfillerCancelled && request.dateCancelled && (
					<>
						<EuiDescriptionListTitle>Cancelled</EuiDescriptionListTitle>
						<EuiDescriptionListDescription>
							{formatDateTime( request.dateCancelled )}
							{request.cancelledByUser && (
								<> by {request.cancelledByUser.firstName} {request.cancelledByUser.lastName}</>
							)}
						</EuiDescriptionListDescription>
					</>
				)}
			</EuiDescriptionList>
		</FullHeightPanel>
	)
}
